import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { fetchApi } from './fetchApi';

interface ArtikelFormularDaten {
  sku: string;
  bezeichnung: string;
  beschreibung: string;
  einkaufspreis: string;
  verkaufspreis: string;
  kategorie_id: string;

  // Amazon / Shopify (Parent-spezifisch)
  asin: string;
  amazon_sku: string;
  amazon_product_id: string;
  shopify_product_id: string;

  // Lieferant (Dropdown)
  lieferant_id: string;

  // Marke
  marke: string;
}

interface Kategorie {
  id: number;
  name: string;
}

interface Lieferant {
  id: number;
  firmenname: string;
}

export default function ArtikelFormular() {
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<boolean>(false);
  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [lieferanten, setLieferanten] = useState<Lieferant[]>([]);

  const [artikel, setArtikel] = useState<ArtikelFormularDaten>({
    sku: '',
    bezeichnung: '',
    beschreibung: '',
    einkaufspreis: '',
    verkaufspreis: '',
    kategorie_id: '',

    asin: '',
    amazon_sku: '',
    amazon_product_id: '',
    shopify_product_id: '',

    lieferant_id: '',

    marke: ''
  });

  useEffect(() => {
    async function ladeStammdaten() {
      try {
        const resKat = await fetchApi('/api/kategorie');
        const dataKat = await resKat.json();
        setKategorien(Array.isArray(dataKat) ? dataKat : []);
      } catch (err) {
        console.error('Fehler beim Laden der Kategorien:', err);
      }

      try {
        const resLief = await fetchApi('/api/lieferant');
        const dataLief = await resLief.json();
        setLieferanten(Array.isArray(dataLief) ? dataLief : []);
      } catch (err) {
        console.error('Fehler beim Laden der Lieferanten:', err);
      }
    }

    ladeStammdaten();
  }, []);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setArtikel(prev => ({ ...prev, [name]: value }));
  }

  function handleKategorieChange(e: SelectChangeEvent) {
    setArtikel(prev => ({ ...prev, kategorie_id: e.target.value }));
  }

  function handleLieferantChange(e: SelectChangeEvent) {
    setArtikel(prev => ({ ...prev, lieferant_id: e.target.value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    setErfolg(false);

    const payload = {
      sku: artikel.sku,
      ean: null,
      bezeichnung: artikel.bezeichnung,
      beschreibung: artikel.beschreibung || null,

      // Marke
      marke: artikel.marke || null,

      // Parent-spezifische Regeln: immer NULL
      farbe: null,
      groesse: null,
      mindestbestand: null,

      einkaufspreis: artikel.einkaufspreis ? parseFloat(artikel.einkaufspreis) : null,
      verkaufspreis: artikel.verkaufspreis ? parseFloat(artikel.verkaufspreis) : null,

      // ✅ Parent hat kein eindeutig zuordenbares Bild → immer NULL
      bild_url: null,

      kategorie_id: artikel.kategorie_id ? parseInt(artikel.kategorie_id, 10) : null,

      // Parent-spezifisch
      parent_id: null,

      // Lieferant (optional)
      lieferant_id: artikel.lieferant_id ? parseInt(artikel.lieferant_id, 10) : null,

      // Amazon / Shopify Felder (Parent-Ebene)
      asin: artikel.asin || null,
      amazon_sku: artikel.amazon_sku || null,
      amazon_product_id: artikel.amazon_product_id || null,
      shopify_product_id: artikel.shopify_product_id || null,

      // Child-spezifische Felder bei Parent IMMER NULL
      fnsku: null,
      shopify_variant_id: null,

      // Internes Feld reserviert
      barcode_intern: null
    };

    try {
      const res = await fetchApi('/api/artikel', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Fehler beim Speichern');
      }

      setErfolg(true);
      setArtikel({
        sku: '',
        bezeichnung: '',
        beschreibung: '',
        einkaufspreis: '',
        verkaufspreis: '',
        kategorie_id: '',

        asin: '',
        amazon_sku: '',
        amazon_product_id: '',
        shopify_product_id: '',

        lieferant_id: '',

        marke: ''
      });
    } catch (err: any) {
      if (err?.message === '401') {
        setFehler('Session abgelaufen. Bitte neu einloggen.');
      } else {
        setFehler(err.message || 'Unbekannter Fehler');
      }
    }
  }

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={2}>
        <Typography variant="h5">Parent-Artikel anlegen</Typography>
      </Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}
      {erfolg && <Alert severity="success" sx={{ mb: 2 }}>Artikel erfolgreich gespeichert</Alert>}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Basisdaten */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="SKU"
              name="sku"
              value={artikel.sku}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Bezeichnung"
              name="bezeichnung"
              value={artikel.bezeichnung}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          {/* Marke */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="marke-label">Marke</InputLabel>
              <Select
                labelId="marke-label"
                id="marke"
                name="marke"
                value={artikel.marke}
                label="Marke"
                onChange={(e: SelectChangeEvent) =>
                  setArtikel(prev => ({ ...prev, marke: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>Keine Marke</em>
                </MenuItem>
                <MenuItem value="Haairay!">Haairay!</MenuItem>
                <MenuItem value="ufash">ufash</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Beschreibung"
              name="beschreibung"
              value={artikel.beschreibung}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>

          {/* Preise */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Einkaufspreis"
              name="einkaufspreis"
              value={artikel.einkaufspreis}
              onChange={handleChange}
              fullWidth
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Verkaufspreis"
              name="verkaufspreis"
              value={artikel.verkaufspreis}
              onChange={handleChange}
              fullWidth
              type="number"
            />
          </Grid>

          {/* Kategorie */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel id="kategorie-label">Kategorie</InputLabel>
              <Select
                labelId="kategorie-label"
                id="kategorie_id"
                name="kategorie_id"
                value={artikel.kategorie_id}
                label="Kategorie"
                onChange={handleKategorieChange}
              >
                {kategorien.map((kat) => (
                  <MenuItem key={kat.id} value={kat.id.toString()}>
                    {kat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Lieferant */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="lieferant-label">Lieferant</InputLabel>
              <Select
                labelId="lieferant-label"
                id="lieferant_id"
                value={artikel.lieferant_id}
                label="Lieferant"
                onChange={handleLieferantChange}
              >
                {lieferanten.map((l) => (
                  <MenuItem key={l.id} value={l.id.toString()}>
                    {l.firmenname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Amazon / Shopify Felder */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="ASIN"
              name="asin"
              value={artikel.asin}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amazon SKU"
              name="amazon_sku"
              value={artikel.amazon_sku}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amazon Product ID"
              name="amazon_product_id"
              value={artikel.amazon_product_id}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Shopify Product ID"
              name="shopify_product_id"
              value={artikel.shopify_product_id}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" color="primary" type="submit">
              Speichern
            </Button>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
}
